import dayjs from 'dayjs';
import _ from 'lodash';
import Tooltip from 'rc-tooltip';
import React from 'react';
import { CSVLink } from 'react-csv';
import { FiArrowRight, FiSearch } from 'react-icons/fi';
import { CircleSpinner } from 'react-spinners-kit';
import styled from 'styled-components';
import { images } from '../../../assets/images';
import { EditSvg } from '../../../assets/svg-icon-components/EditSvg';
import { useUserRoles } from '../../../util/use-user-role';
import { DashboardHeader } from '../../core/DashboardHeader';
import { DashboardWrapper } from '../../core/DashboardWrapper';
import { HorizontalContent } from '../../core/HorizontalContent';
import { InviteModal } from '../../core/InviteModal';
import { Can } from '../../core/rbac/Can';
import { RhButton, RhButtonMode } from '../../core/RhButton';
import { RhCard } from '../../core/RhCard';
import { RhInput } from '../../core/RhInput';
import { RhTabBar } from '../../core/RhTabBar';
import { colors, H2, Regular } from '../../core/Styles';
import { PeopleProps } from '../containers/PeopleContainer';

export const PeopleScreen: React.FC<PeopleProps> = props => {
  const { roles } = useUserRoles();

  const getDisplayChurchKey = (churchKey?: string | null) => {
    if (!churchKey) return '-';
    const parts = churchKey.split('.');
    if (parts.length >= 2) return parts.slice(1).join('.');
    return churchKey;
  };

  return (
    <DashboardWrapper>
      <DashboardHeader
        title={<RhTabBar activeId={props.currentTabId} tabs={props.tabs} onSelect={props.userSelectedTab} />}
        actions={
          <Can
            role={roles?.[0]}
            perform="create:users"
            yes={[
              <RhButton key="add" title="Invite Someone" onClick={props.userToggledInviteModal} />,
              <CSVLink
                key="export"
                data={props.csvData}
                filename={'users.csv'}
                target="_blank"
                // style override for generated <a /> - no need to add styled(CSVLink)...
                style={{ textDecoration: 'none' }}>
                <RhButton mode={RhButtonMode.text} title="Export List" suffixIcon={FiArrowRight} />
              </CSVLink>,
            ]}
          />
        }
      />
      <RhCard>
        <HorizontalContent
          left={<H2>People</H2>}
          right={[
            props.isLoadingUsers && <CircleSpinner key="spinner" size={15} color={colors.primary} />,
            <RhInput
              onChange={e => {
                props.userUpdatedSearchTerm(e.target.value);
              }}
              key="search-box"
              icon={FiSearch}
              placeholder="Search"
            />,
          ]}
        />
        <List>
          <ListHeader>
            <ListHeaderItem span={8}>Name</ListHeaderItem>
            <ListHeaderItem span={8}>Email</ListHeaderItem>
            <ListHeaderItem span={8}>Role</ListHeaderItem>
            <ListHeaderItem span={8}>Church</ListHeaderItem>
            <ListHeaderItem span={8}>Active Since</ListHeaderItem>
            {/* Actions column, [children] is supposed to be empty */}
            <ListHeaderItem span={1}></ListHeaderItem>
          </ListHeader>
          <ListBody>
            {!props.users.length ? (
              <EmptyStateDiv>
                <Regular>No users match the role</Regular>
              </EmptyStateDiv>
            ) : (
              props.users.map(user => {
                return (
                  <div key={user.id}>
                    <ListItem>
                      <ListItemColumn span={8}>
                        <ListItemColumnAvatar src={user.imagePath || images.fallbackImage} />
                        <span>{_.startCase(user.name) || '-'}</span>
                      </ListItemColumn>
                      <ListItemColumn span={8}>
                        <span>{user.email || '-'}</span>
                      </ListItemColumn>
                      <ListItemColumn span={8}>{user.role || '-'}</ListItemColumn>
                      <ListItemColumn span={8}>{getDisplayChurchKey(user.churchKey)}</ListItemColumn>
                      <ListItemColumn span={8}>{dayjs(user.created).format('MMMM DD, YYYY')}</ListItemColumn>
                      <ListItemColumn span={1} justify="flex-end">
                        <Can
                          role={roles?.[0]}
                          perform="update:users"
                          yes={
                            <Tooltip
                              placement="left"
                              overlay={
                                <Regular color={colors.background} weight="600">
                                  View/Edit/Delete User
                                </Regular>
                              }>
                              <EditButtonHoverEffect>
                                <RhButton
                                  onClick={() => props.userClickedEditPerson(user.id)}
                                  mode={RhButtonMode.subtle}
                                  prefixSvg={EditSvg}
                                  iconColor={colors.grey}
                                />
                              </EditButtonHoverEffect>
                            </Tooltip>
                          }
                        />
                      </ListItemColumn>
                    </ListItem>
                    <ListItemHorizontalDivider />
                  </div>
                );
              })
            )}
          </ListBody>
        </List>
      </RhCard>

      <Can
        role={roles?.[0]}
        perform="create:users"
        yes={
          <InviteModal
            isLoading={props.isInviteModalLoading}
            isOpen={props.isInviteModalOpen}
            onRequestClose={props.userToggledInviteModal}
            onOK={props.userSubmittedInvites}
          />
        }
      />
    </DashboardWrapper>
  );
};

const List = styled.div`
  margin-top: 10px;
  margin-left: -23px;
  margin-right: -16px;
`;

const ListHeader = styled.div`
  display: flex;
  border-bottom: 1px solid ${colors.faintGrey};
  margin-left: 23px;
  margin-right: 16px;
  & > :last-child {
    padding-right: 14px;
  }
`;

const ListHeaderItem = styled.div<{ span: number }>`
  flex: ${props => props.span};
  font-weight: 600;
  font-size: 12px;
  line-height: 18px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: ${colors.grey};
  padding: 8px 0;
`;

const ListBody = styled.div`
  overflow-y: auto;
  max-height: 65vh;
`;

const ListItem = styled.div`
  display: flex;
  font-weight: 600;
  font-size: 14px;
  line-height: 18px;
  color: ${colors.normal};
  padding: 8px 16px 8px 23px;
  & > :last-child {
    padding-right: 14px;
  }
  &:hover {
    background-color: ${colors.background};
  }
  & > :not(:last-child) {
    margin-right: 16px;
  }
`;

const ListItemColumn = styled.div<{ span: number; justify?: 'flex-start' | 'center' | 'flex-end' }>`
  display: flex;
  flex: ${props => props.span};
  align-items: center;
  justify-content: ${({ justify = 'flex-start' }) => justify};
  & > :not(:last-child) {
    margin-right: 12px;
  }
  min-width: 0;
  & > span {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const ListItemColumnAvatar = styled.img`
  border-radius: 20px;
  width: 40px;
  height: 40px;
  object-fit: cover;
`;

const ListItemHorizontalDivider = styled.div`
  height: 1px;
  background-color: ${colors.faintGrey};
  margin-left: 23px;
  margin-right: 16px;
`;

const EditButtonHoverEffect = styled.div`
  & > button {
    &:hover {
      background-color: ${colors.lightBlue};
      & > svg {
        fill: ${colors.background};
      }
    }
  }
`;

const EmptyStateDiv = styled.div`
  display: flex;
  justify-content: center;
  padding: 16px;
`;
